import { StandardTextfield, useFetch, UseFetchOptions } from '@brandfolder/react';
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import { FunctionComponent, HTMLAttributes, useContext, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import styled from 'styled-components';
import { object, SchemaOf, string } from 'yup';

import { defaultGettyTheme, defaultTheme } from '@constants/theme';
import { BrandguideContext } from '@context/brandguide';
import { ThemeContext } from '@context/theme';
import { ApiDatum } from '@typings/api';
import { Theme, ThemeImage } from '@typings/theme';

interface SettingsFaviconValues {
  faviconHref?: string;
}

/**
 * https://en.wikipedia.org/wiki/Favicon
 */
const extensions = ['.ico', '.png', '.gif', '.jpg', '.jpeg', '.svg'];

const schema: SchemaOf<SettingsFaviconValues> = object().shape({
  faviconHref: string()
    .url('Must be a valid URL')
    .test('is-valid-format', 'Must be one of .ico, .png, .gif, .jpg, .jpeg, or .svg', (value) => {
      if (!value) return true;
      return extensions.some((extension) => value.includes(extension));
    })
});

interface SettingsFaviconProps extends HTMLAttributes<HTMLFormElement> {}

const UnstyledSettingsFavicon: FunctionComponent<SettingsFaviconProps> = (props) => {
  const { brandguide } = useContext(BrandguideContext);
  const { theme, setTheme } = useContext(ThemeContext);

  const ref = useRef('');

  const faviconIsDefault =
    theme.image.faviconHref === defaultTheme.image.faviconHref ||
    theme.image.faviconHref === defaultGettyTheme.image.faviconHref;
  const { formState, handleSubmit, register } = useForm<SettingsFaviconValues>({
    defaultValues: {
      faviconHref: faviconIsDefault ? '' : theme.image.faviconHref
    },
    mode: 'onBlur',
    resolver: yupResolver(schema),
    reValidateMode: 'onBlur',
    shouldFocusError: false
  });

  const { errors } = formState;

  const options: UseFetchOptions = {
    fetchOnMount: false,
    headers: {
      'Content-Type': 'application/json',
      'x-brandguide-key': brandguide?.key || ''
    },
    method: 'PATCH',
    url: `/api/themes/${theme.key}`
  };

  const { error, fetch, loading, response } = useFetch<ApiDatum<Partial<Theme>>>(options);

  useEffect(() => {
    if (loading) {
      ref.current = toast.loading('Saving...');
    }
  }, [loading]);

  useEffect(() => {
    if (response) {
      setTheme({
        ...theme,
        image: {
          ...theme.image,
          faviconHref: response.data.attributes.image?.faviconHref || defaultTheme.image.faviconHref
        }
      });
      toast.dismiss(ref.current);
      toast.success('Saved!');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  useEffect(() => {
    if (error) {
      toast.dismiss(ref.current);
      toast.error('Error saving.');
    }
  }, [error]);

  return (
    <form
      {...props}
      className={classNames({
        'global-settings__wrapper': true,
        [`${props.className}`]: !!props.className
      })}
      noValidate
      onBlur={handleSubmit((values) => {
        const image: ThemeImage = {
          faviconHref: values.faviconHref || '',
          heroSrc: theme.image.heroSrc
        };

        fetch({
          ...options,
          body: {
            data: {
              attributes: {
                image
              }
            }
          }
        });
      })}
    >
      <StandardTextfield
        {...register('faviconHref')}
        className="favicon-textfield"
        error={errors.faviconHref?.message}
        label="Favicon CDN URL"
        id="faviconHref"
      />
    </form>
  );
};

const StyledSettingsFavicon = styled(UnstyledSettingsFavicon)`
  .favicon-textfield {
    margin-top: 24px;
  }
`;

export const SettingsFavicon: FunctionComponent<SettingsFaviconProps> = (props) => {
  return <StyledSettingsFavicon {...props} />;
};
